import { useEffect, useState } from 'preact/hooks';
import { AdminShell, type Overview } from './AdminShell';
import { api } from '../../lib/api';
import { Button } from '../../components/Button';
import {
  Table, Th, Td, Pager, Toolbar, Badge, fmtDate, fieldStyle, type Page, PAGE_SIZE,
} from './ui';
import { GIFT_DURATIONS, useGift } from './gift';

type AdminSub = {
  appUserId: string;
  productId: string;
  entitlement?: string;
  store?: string;
  environment: string;
  status: string;
  periodType?: string;
  purchasedAt: string | null;
  expiresAt: string | null;
  lastEventAt: string | null;
};

type SubsResponse = Page & { subscriptions: AdminSub[] };

const STATUS_TONE: Record<string, 'green' | 'ember' | 'wine' | 'neutral'> = {
  active: 'green',
  trialing: 'ember',
  cancelled: 'neutral',
  expired: 'neutral',
  billing_issue: 'wine',
  paused: 'neutral',
  unknown: 'neutral',
};

export function AdminSubscriptions() {
  return (
    <AdminShell tab="subscriptions">
      {(ov) => (
        <>
          <GiftCard />
          <SubsTable overview={ov} />
        </>
      )}
    </AdminShell>
  );
}

// Grants (or revokes) a promotional `pro` entitlement via RevenueCat, keyed by
// account email. The grant lands in the table below once RevenueCat's webhook
// delivers the corresponding event. (Per-user gifting also lives on the Users
// tab as a row action — both go through the same useGift hook.)
function GiftCard() {
  const [email, setEmail] = useState('');
  const [duration, setDuration] = useState<string>('month');
  const { busy, msg, gift, revoke } = useGift();
  const trimmed = email.trim();

  return (
    <div
      style={{
        background: 'var(--card)', borderRadius: 16, boxShadow: '0 0 0 1px var(--line)',
        padding: 18, marginBottom: 20,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', marginBottom: 12 }}>
        Gift a subscription
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <input
          type="email"
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          placeholder="account email"
          style={{ ...fieldStyle, flex: '1 1 220px' }}
          disabled={busy}
        />
        <select
          value={duration}
          onChange={(e) => setDuration((e.target as HTMLSelectElement).value)}
          style={{ ...fieldStyle, cursor: 'pointer' }}
          disabled={busy}
        >
          {GIFT_DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        <Button
          variant="ember"
          size="sm"
          disabled={busy || !trimmed}
          onClick={() => trimmed && gift(trimmed, duration)}
        >
          Gift
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy || !trimmed}
          onClick={() => trimmed && revoke(trimmed)}
        >
          Revoke gift
        </Button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10, lineHeight: 1.5 }}>
        Grants the <code>pro</code> entitlement as a RevenueCat promotional — no store billing
        involved. The recipient needs a shotmeal account (signed in to the app at least once).
      </div>
      {msg && (
        <div style={{ fontSize: 13, marginTop: 10, color: msg.ok ? '#1c7a45' : 'var(--wine)' }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

function SubsTable({ overview }: { overview: Overview | null }) {
  const [rows, setRows] = useState<AdminSub[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    api<SubsResponse>(`/api/admin/subscriptions?${params}`)
      .then((r) => { if (!cancelled) { setRows(r.subscriptions); setTotal(r.total); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [offset, search, status]);

  // Empty + nothing ever ingested → the webhook probably isn't wired yet.
  const neverIngested = !loading && !error && overview?.subscriptions.total === 0 && rows.length === 0;

  return (
    <>
      <Toolbar
        search={search}
        onSearch={(v) => { setOffset(0); setSearch(v); }}
        placeholder="Search by app user / product…"
        filters={[
          {
            value: status,
            onChange: (v) => { setOffset(0); setStatus(v); },
            options: [
              { value: '', label: 'All statuses' },
              { value: 'active', label: 'Active' },
              { value: 'trialing', label: 'Trialing' },
              { value: 'cancelled', label: 'Cancelled' },
              { value: 'expired', label: 'Expired' },
              { value: 'billing_issue', label: 'Billing issue' },
            ],
          },
        ]}
      />

      {error && <p style={{ color: 'var(--wine)' }}>{error}</p>}

      {neverIngested && (
        <div
          style={{
            background: 'var(--surface-2)', borderRadius: 14, padding: 18,
            marginBottom: 16, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6,
          }}
        >
          No subscription events yet. This list is populated by RevenueCat
          webhooks — configure a webhook pointing at{' '}
          <code style={{ background: 'var(--card)', padding: '2px 6px', borderRadius: 6 }}>
            /api/webhooks/revenuecat
          </code>{' '}
          with the <code>REVENUECAT_WEBHOOK_AUTH</code> header to start receiving them.
        </div>
      )}

      <Table>
        <thead>
          <tr>
            <Th>App user</Th>
            <Th>Product</Th>
            <Th>Status</Th>
            <Th>Store</Th>
            <Th>Expires</Th>
            <Th>Last event</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={`${s.appUserId}:${s.productId}`}>
              <Td>
                <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{s.appUserId}</span>
                {s.environment === 'sandbox' && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink-3)' }}>sandbox</span>
                )}
              </Td>
              <Td>
                {s.productId}
                {s.periodType === 'trial' && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink-3)' }}>trial</span>
                )}
              </Td>
              <Td><Badge tone={STATUS_TONE[s.status] ?? 'neutral'}>{s.status}</Badge></Td>
              <Td muted>{s.store ?? '—'}</Td>
              <Td muted>{fmtDate(s.expiresAt)}</Td>
              <Td muted>{fmtDate(s.lastEventAt)}</Td>
            </tr>
          ))}
          {!loading && rows.length === 0 && !neverIngested && (
            <tr><Td colSpan={6} muted>No subscriptions found.</Td></tr>
          )}
        </tbody>
      </Table>

      <Pager total={total} offset={offset} count={rows.length} loading={loading} onOffset={setOffset} />
    </>
  );
}
